import { Box, Typography, FormControlLabel, Checkbox, FormControl, Select, MenuItem } from "@mui/material"
import { isDoublePowerMoveId, isHakidasuMove, isHundonokobushiMove, isKishikaiseiMove, isOhakamairiMove, isRenzokuMove, isStoredPowerMove } from "../../../move/move"
import { AttackPokemonData } from "../../../types/attack"
import StatusAilmentSelect, { STATUS_AILMENT_OPTIONS, StatusAilmentKey } from "../../../components/StatusAilmentSelect"

interface OffencePokemonOtherSettingInputProps {
  attackPokemon: AttackPokemonData
  handleUpdateAttackPokemonStatus: (id: string, isBurned: boolean, isPoisoned: boolean, isParalyzed: boolean) => void
  handleUpdateCriticalHit: (id: string, isCritical: boolean, criticalHitCount?: number) => void
  handleUpdateCustomPower: (id: string, customPower: number) => void
  handleUpdateJuuden: (id: string, isJuuden: boolean) => void
  handleUpdateDoublePower: (id: string, isDoublePower: boolean) => void
}

function OffencePokemonOtherSettingInput({
  attackPokemon,
  handleUpdateAttackPokemonStatus,
  handleUpdateCriticalHit,
  handleUpdateCustomPower,
  handleUpdateJuuden,
  handleUpdateDoublePower
}: OffencePokemonOtherSettingInputProps) {
  if (!attackPokemon.selectedMoveId) {
    return null;
  }

  // 威力2倍条件の対象技ID
  const isDoublePowerMove = isDoublePowerMoveId(attackPokemon.selectedMoveId);
  const statusFlags: Record<(typeof STATUS_AILMENT_OPTIONS)[number]['key'], boolean> = {
    poisoned: Boolean(attackPokemon.isPoisoned),
    burned: Boolean(attackPokemon.isBurned),
    paralyzed: Boolean(attackPokemon.isParalyzed),
  };
  const selectedStatus =
    STATUS_AILMENT_OPTIONS.find((status) => statusFlags[status.key])?.key ?? 'none';

  const handleStatusChange = (status: StatusAilmentKey | 'none') => {
    handleUpdateAttackPokemonStatus(
      attackPokemon.id,
      status === 'burned',
      status === 'poisoned',
      status === 'paralyzed',
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
        その他
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {/* 状態異常 */}
        <StatusAilmentSelect
          value={selectedStatus}
          onChange={handleStatusChange}
          width="12ch"
          stopPropagation
        />

        {/* Critical Hit Selection */}
        {isRenzokuMove(attackPokemon.selectedMove) ? (
          // 連続技の場合は急所回数を選択
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
              急所
            </Typography>
            <FormControl size="small" sx={{ minWidth: 45 }}>
              <Select
                value={attackPokemon.criticalHitCount || 0}
                onChange={(e) => {
                  const criticalHitCount = Number(e.target.value);
                  handleUpdateCriticalHit(attackPokemon.id, criticalHitCount > 0, criticalHitCount);
                }}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  fontSize: '0.6rem',
                  '& .MuiSelect-select': {
                    padding: '2px 4px',
                    textAlign: 'center'
                  }
                }}
              >
                {(() => {
                  // 連続技の最大回数を取得。技固有の最大回数を正確に反映
                  const maxHits = attackPokemon.selectedMove?.max_renzoku || 5;
                  return Array.from({ length: maxHits + 1 }, (_, i) => (
                    <MenuItem key={i} value={i} sx={{ fontSize: '0.6rem', justifyContent: 'center' }}>
                      {i}回
                    </MenuItem>
                  ));
                })()}
              </Select>
            </FormControl>
          </Box>
        ) : (
          // 単発技の場合はチェックボックス
          <FormControlLabel
            control={
              <Checkbox
                checked={attackPokemon.isCritical || false}
                onChange={(e) => {
                  handleUpdateCriticalHit(attackPokemon.id, e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={{ padding: '2px' }}
              />
            }
            label="急所"
            sx={{
              fontSize: '0.6rem',
              '& .MuiFormControlLabel-label': { fontSize: '0.6rem' }
            }}
          />
        )}

        {/* じゅうでん */}
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={attackPokemon.isJuuden || false}
              onChange={(e) => {
                handleUpdateJuuden(attackPokemon.id, e.target.checked);
              }}
              sx={{ padding: '2px' }}
            />
          }
          label="じゅうでん"
          sx={{
            fontSize: '0.6rem',
            '& .MuiFormControlLabel-label': { fontSize: '0.6rem' }
          }}
        />

        {/* 威力2倍条件 */}
        {isDoublePowerMove && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={attackPokemon.isDoublePower || false}
                onChange={(e) => {
                  handleUpdateDoublePower(attackPokemon.id, e.target.checked);
                }}
                sx={{ padding: '2px' }}
              />
            }
            label="威力2倍"
            sx={{
              fontSize: '0.6rem',
              '& .MuiFormControlLabel-label': { fontSize: '0.6rem' }
            }}
          />
        )}

        {/* きしかいせい・じたばたの威力選択 */}
        {attackPokemon.selectedMove && isKishikaiseiMove(attackPokemon.selectedMove) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
              威力
            </Typography>
            <FormControl size="small" sx={{ minWidth: 60 }}>
              <Select
                value={attackPokemon.customPower || 20}
                onChange={(e) => {
                  handleUpdateCustomPower(attackPokemon.id, Number(e.target.value));
                }}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  fontSize: '0.6rem',
                  '& .MuiSelect-select': {
                    padding: '2px 4px',
                    textAlign: 'center'
                  }
                }}
              >
                <MenuItem value={20} sx={{ fontSize: '0.6rem', justifyContent: 'center' }}>20</MenuItem>
                <MenuItem value={40} sx={{ fontSize: '0.6rem', justifyContent: 'center' }}>40</MenuItem>
                <MenuItem value={80} sx={{ fontSize: '0.6rem', justifyContent: 'center' }}>80</MenuItem>
                <MenuItem value={100} sx={{ fontSize: '0.6rem', justifyContent: 'center' }}>100</MenuItem>
                <MenuItem value={150} sx={{ fontSize: '0.6rem', justifyContent: 'center' }}>150</MenuItem>
                <MenuItem value={200} sx={{ fontSize: '0.6rem', justifyContent: 'center' }}>200</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {/* おはかまいりの威力選択 */}
        {attackPokemon.selectedMove && isOhakamairiMove(attackPokemon.selectedMove) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
              威力
            </Typography>
            <FormControl size="small" sx={{ minWidth: 60 }}>
              <Select
                value={attackPokemon.customPower || 50}
                onChange={(e) => {
                  handleUpdateCustomPower(attackPokemon.id, Number(e.target.value));
                }}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  fontSize: '0.6rem',
                  '& .MuiSelect-select': {
                    padding: '2px 4px',
                    textAlign: 'center'
                  }
                }}
              >
                {Array.from({ length: 7 }, (_, i) => {
                  const power = 50 + (i * 50);
                  return (
                    <MenuItem key={power} value={power} sx={{ fontSize: '0.6rem', justifyContent: 'center' }}>
                      {power}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* ふんどのこぶしの威力選択 */}
        {attackPokemon.selectedMove && isHundonokobushiMove(attackPokemon.selectedMove) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
              威力
            </Typography>
            <FormControl size="small" sx={{ minWidth: 60 }}>
              <Select
                value={attackPokemon.customPower || 50}
                onChange={(e) => {
                  handleUpdateCustomPower(attackPokemon.id, Number(e.target.value));
                }}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  fontSize: '0.6rem',
                  '& .MuiSelect-select': {
                    padding: '2px 4px',
                    textAlign: 'center'
                  }
                }}
              >
                {Array.from({ length: 7 }, (_, i) => {
                  const power = 50 + (i * 50);
                  return (
                    <MenuItem key={power} value={power} sx={{ fontSize: '0.6rem', justifyContent: 'center' }}>
                      {power}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* アシストパワー・つけあがるの威力選択 */}
        {attackPokemon.selectedMove && isStoredPowerMove(attackPokemon.selectedMove) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
              威力
            </Typography>
            <FormControl size="small" sx={{ minWidth: 60 }}>
              <Select
                value={attackPokemon.customPower || 20}
                onChange={(e) => {
                  handleUpdateCustomPower(attackPokemon.id, Number(e.target.value));
                }}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  fontSize: '0.6rem',
                  '& .MuiSelect-select': {
                    padding: '2px 4px',
                    textAlign: 'center'
                  }
                }}
              >
                {Array.from({ length: 43 }, (_, i) => {
                  const power = 20 + (i * 20);
                  return (
                    <MenuItem key={power} value={power} sx={{ fontSize: '0.6rem', justifyContent: 'center' }}>
                      {power}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* はきだすの威力選択 */}
        {attackPokemon.selectedMove && isHakidasuMove(attackPokemon.selectedMove) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
              威力
            </Typography>
            <FormControl size="small" sx={{ minWidth: 60 }}>
              <Select
                value={attackPokemon.customPower || 100}
                onChange={(e) => {
                  handleUpdateCustomPower(attackPokemon.id, Number(e.target.value));
                }}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  fontSize: '0.6rem',
                  '& .MuiSelect-select': {
                    padding: '2px 4px',
                    textAlign: 'center'
                  }
                }}
              >
                <MenuItem value={100} sx={{ fontSize: '0.6rem', justifyContent: 'center' }}>100</MenuItem>
                <MenuItem value={200} sx={{ fontSize: '0.6rem', justifyContent: 'center' }}>200</MenuItem>
                <MenuItem value={300} sx={{ fontSize: '0.6rem', justifyContent: 'center' }}>300</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default OffencePokemonOtherSettingInput
